import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, RefreshCw, Loader2, CheckCircle2, AlertCircle, TrendingUp, Star, AlertTriangle, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCMAUpdateMutation, type CMA } from "@/hooks/useCMAData";

function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.911 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.182a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .511 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.041l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95A4.5 4.5 0 0 1 3.6 18.304zm-1.26-10.43A4.485 4.485 0 0 1 4.704 5.9l.001 5.7a.766.766 0 0 0 .388.677l5.814 3.354-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.787A4.504 4.504 0 0 1 2.34 7.874zm16.597 3.855-5.785-3.339 2.015-1.163a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.104v-5.677a.79.79 0 0 0-.407-.667l-.048-.049zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.41 9.228V6.896a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.037a4.5 4.5 0 0 1 7.376-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
    </svg>
  );
}

interface CMAAnalysisProps {
  cma: CMA;
}

export function CMAAnalysis({ cma }: CMAAnalysisProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [agentIntel, setAgentIntel] = useState(cma.agent_intel || "");
  const [isSavingIntel, setIsSavingIntel] = useState(false);
  const updateCMA = useCMAUpdateMutation(cma.id);

  const structured = cma.ai_analysis_structured;

  const handleSaveIntel = async () => {
    setIsSavingIntel(true);
    try {
      await updateCMA.mutateAsync({ agent_intel: agentIntel } as any);
      toast({ title: "Intel saved" });
    } catch {
      toast({ title: "Error", description: "Failed to save intel", variant: "destructive" });
    } finally {
      setIsSavingIntel(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      // Auto-save latest intel so the edge function picks it up
      await updateCMA.mutateAsync({ agent_intel: agentIntel } as any);

      const { data, error } = await supabase.functions.invoke("generate-cma-analysis", {
        body: { cmaId: cma.id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Analysis generated!", description: "AI pricing analysis is ready." });
      queryClient.invalidateQueries({ queryKey: ["cma", cma.id] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate analysis";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const agentIntelCard = (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-600" />
          Agent Intel
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Share what you know — seller motivation, timeline pressure, competing listings, recent improvements. This gets sent directly to the AI.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          placeholder={`e.g. "Seller needs to close by June 30" or "New roof and HVAC in 2023" or "Two competing listings just reduced on the same street"`}
          value={agentIntel}
          onChange={(e) => setAgentIntel(e.target.value)}
          className="min-h-[90px] resize-none bg-white text-sm"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {agentIntel.trim() ? "Intel will be included in the next generation." : "No intel added yet."}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveIntel}
            disabled={isSavingIntel}
            className="gap-1.5 h-7 text-xs"
          >
            {isSavingIntel ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save Intel
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (!structured && !cma.ai_analysis) {
    return (
      <div className="space-y-4">
        {agentIntelCard}
        <Card>
          <CardContent className="p-6 text-center">
            <Sparkles className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="font-medium mb-1">No analysis yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Generate an AI-powered CMA analysis with pricing recommendation, market summary, and strategic insights.
            </p>
            <Button onClick={handleGenerate} disabled={isGenerating} variant="gold" className="gap-2">
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {isGenerating ? "Generating..." : "Generate Analysis"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {agentIntelCard}

      {/* Regenerate button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={isGenerating}
          className="gap-2"
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isGenerating ? "Regenerating..." : "Regenerate"}
        </Button>
      </div>

      {/* Market Summary */}
      {structured?.marketSummary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Market Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{structured.marketSummary}</p>
          </CardContent>
        </Card>
      )}

      {/* Pricing Analysis */}
      {structured?.pricingAnalysis && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pricing Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{structured.pricingAnalysis}</p>
          </CardContent>
        </Card>
      )}

      {/* Key Selling Points & Competitive Factors */}
      <div className="grid md:grid-cols-2 gap-4">
        {structured?.keySellingPoints?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                Key Selling Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {structured.keySellingPoints.map((point: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {structured?.competitiveFactors?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Competitive Factors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {structured.competitiveFactors.map((factor: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* AI Recommendation */}
      {structured?.agentRecommendation && (
        <Card className="border-l-4" style={{ borderLeftColor: "#10b981" }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              AI Recommendation
              <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground border border-border rounded-full px-2 py-0.5 ml-1">
                <OpenAILogo className="h-3 w-3" />
                OpenAI
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{structured.agentRecommendation}</p>
          </CardContent>
        </Card>
      )}

      {/* Fallback: raw AI analysis */}
      {!structured && cma.ai_analysis && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">AI Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{cma.ai_analysis}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
